import type { ChangeEvent, ReactNode } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useToast } from '@/components/kit/toast'
import { CameraCapture } from '@/components/entry/camera-capture'
import { VEHICLE_PHOTO_SIZE, fileToPhoto } from '@/lib/carwash/image'

/*
 * Everything needed to add a photo from anywhere: the live camera, the
 * phone's own camera app and the gallery. Render `elements` once and call
 * openCamera or openGallery from any button.
 */
export const usePhotoCapture = ({ onPhoto, maxSize = VEHICLE_PHOTO_SIZE }: { onPhoto: (dataUrl: string) => void, maxSize?: number }) => {
    const toast = useToast()
    const cameraInputRef = useRef<HTMLInputElement>(null)
    const galleryInputRef = useRef<HTMLInputElement>(null)
    const onPhotoRef = useRef(onPhoto)
    const [cameraOpen, setCameraOpen] = useState(false)
    const [busy, setBusy] = useState(false)

    useEffect(() => {
        onPhotoRef.current = onPhoto
    }, [onPhoto])

    const openCamera = useCallback(() => {
        if (navigator.mediaDevices?.getUserMedia) setCameraOpen(true)
        else cameraInputRef.current?.click()
    }, [])

    const openGallery = useCallback(() => galleryInputRef.current?.click(), [])

    const closeCamera = useCallback(() => setCameraOpen(false), [])

    const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return

        setBusy(true)

        try {
            onPhotoRef.current(await fileToPhoto(file, maxSize))
        }
        catch (error) {
            toast({ tone: 'error', title: error instanceof Error ? error.message : 'This photo could not be used.' })
        }
        finally {
            setBusy(false)
        }
    }

    const elements: ReactNode = (
        <>
            <input ref={cameraInputRef} type='file' accept='image/*' capture='environment' className='hidden' onChange={handleFile} tabIndex={-1} aria-hidden='true' />
            <input ref={galleryInputRef} type='file' accept='image/*' className='hidden' onChange={handleFile} tabIndex={-1} aria-hidden='true' />

            <CameraCapture
                open={cameraOpen}
                onClose={closeCamera}
                onCapture={dataUrl => {
                    setCameraOpen(false)
                    onPhotoRef.current(dataUrl)
                }}
                onUseDeviceCamera={() => {
                    setCameraOpen(false)
                    cameraInputRef.current?.click()
                }}
                onUseGallery={() => {
                    setCameraOpen(false)
                    galleryInputRef.current?.click()
                }}
            />
        </>
    )

    return { openCamera, openGallery, busy, elements }
}
